import React, { useEffect, useState, useCallback, useRef } from 'react';
import { styled, useTheme } from '@mui/system';
import { useMediaQuery } from '@mui/material';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper/index';
import List from '@mui/material/List/index';
import { Preloader } from '#component/preloader';
import { groupEvents } from './helper';
import EventsList from './events-list';
import { deepEqual, debounce } from '#helper';

const TitleTypography = styled(Typography)(() => ({
  margin: '12px 0 12px 16px',
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  '& span': {
    marginLeft: '8px'
  }
}));

const NoDataContainer = styled('div')(() => ({
  marginTop: 150,
  textAlign: 'center',
  '& h4': {
    color: '#667'
  }
}));

const EventsContainer = styled(Paper)(() => ({
  display: 'block'
}));

const EventsListContainer = styled(List)(() => ({
  height: '100%'
}));

function useDeepCompareMemoize(events) {
  const ref = useRef();

  if (!deepEqual(events, ref.current)) {
    ref.current = events;
  }

  return ref.current;
}

export default function DeviceEvents(props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [groupedEvents, setGroupedEvents] = useState([]);

  const {
    events,
    account,
    labelWithIcon,
    handleEventClick,
    handleEventHover,
    handleEventGroupHover,
    handleEventGroupClosing,
    displayNoEventsMessage = true,
    syncSelectedDevicesWithStore,
    loading
  } = props;

  const memoizedEvents = useDeepCompareMemoize(events);

  useEffect(() => setGroupedEvents(groupEvents(memoizedEvents)), [memoizedEvents]);

  const debounceClickHandler = debounce((e, item) => {
    handleEventClick(e, item);
  }, 250);

  const handleClick = useCallback((e, item) =>
    handleEventClick && debounceClickHandler(e, item), [handleEventClick, debounceClickHandler]);
  const handleGroupHover = useCallback(item => e =>
    handleEventGroupHover && handleEventGroupHover(e, item), [handleEventGroupHover]);
  const handleHover = useCallback((e, item) =>
    handleEventHover && handleEventHover(e, item), [handleEventHover]);
  const handleGroupClosing = useCallback((e, item) =>
    handleEventGroupClosing && handleEventGroupClosing(e, item), [handleEventGroupClosing]);

  if (!events.length && loading) {
    return (
      <>
        {labelWithIcon &&
        <TitleTypography variant="button" gutterBottom>
          {labelWithIcon}
        </TitleTypography>}
        <EventsContainer className="events">
          <EventsListContainer dense={false}>
            <Preloader />
          </EventsListContainer>
        </EventsContainer>
      </>
    );
  }

  if (!events.length) {
    if (!displayNoEventsMessage) {
      return null;
    }

    return (
      <NoDataContainer>
        <Typography variant="h4" gutterBottom>
          No events
        </Typography>
      </NoDataContainer>
    );
  }

  return (
    <>
      {labelWithIcon &&
      <TitleTypography variant="button" gutterBottom>
        {labelWithIcon}
      </TitleTypography>}
      <EventsList
        account={account}
        isMobile={isMobile}
        groupedEvents={groupedEvents}
        handleClick={handleClick}
        handleHover={handleHover}
        handleGroupHover={handleGroupHover}
        handleEventHover={handleEventHover}
        handleGroupClosing={handleGroupClosing}
        loading={loading}
        syncSelectedDevicesWithStore={syncSelectedDevicesWithStore}
      />
    </>
  );
}
